import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Check, ShoppingBag } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { MEASURE_FIELDS } from "@/lib/measure";

export default function CartPage() {
  const { items, update, remove, subtotal } = useCart();
  const { lang } = useLang();
  const ar = lang === "ar";
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center fade-in">
        <ShoppingBag className="w-12 h-12 mx-auto text-accent mb-5" strokeWidth={1.2} />
        <p className="text-text-muted mb-6" data-testid="empty-cart">{ar ? "سلة التسوق فارغة — اختاري موديلاً وابدئي طلبك حسب مقاسك." : "Your cart is empty — pick a design and start your made-to-measure order."}</p>
        <Link to="/shop" data-testid="empty-cart-shop" className="btn-primary px-8 py-3 text-sm">{ar ? "عودة إلى المتجر" : "Back to shop"}</Link>
      </div>
    );
  }

  const first = items[0];

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12 fade-in">
      <h1 className="font-display text-3xl mb-2">{ar ? "السلة" : "Cart"}</h1>
      <p className="text-xs text-text-muted mb-6">{ar ? "كل قطعة تُفصّل حسب مقاساتها وبياناتها." : "Each piece is tailored to its own measurements and details."}</p>

      <div className="space-y-4">
        {items.map((i) => {
          const c = i.customer || {};
          return (
            <div key={i.lid} data-testid={`cart-item-${i.product_id}`} className="border border-border p-4">
              <div className="flex gap-4">
                <div className="w-20 h-28 bg-surface overflow-hidden flex-shrink-0">{i.image && <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between">
                    <h3 className="text-sm">{i.name_ar} <span className="text-text-muted text-xs">· {i.sku}</span></h3>
                    <button onClick={() => remove(i.lid)} data-testid={`cart-remove-${i.product_id}`} className="text-text-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <p className="text-sm mt-1">{formatKWD(i.price)}</p>
                  <p className="text-xs text-text-muted mt-1" data-testid={`cart-measure-${i.product_id}`}>{MEASURE_FIELDS.map((f) => `${ar ? f.ar : f.en || f.ar} ${i.measurements[f.key]}`).join(" · ")} {ar ? "سم" : "cm"}</p>
                </div>
              </div>
              <div className="mt-3 text-xs text-text-muted border-t border-border pt-3 space-y-0.5">
                <div>{ar ? "العميلة" : "Customer"}: {c.name} · <span dir="ltr">{c.phone}</span></div>
                <div>{ar ? "العنوان" : "Address"}: {c.country === "KW" ? `${ar ? "الكويت" : "Kuwait"} / ${c.governorate} / ${c.region}` : `${c.city || ""}${c.state ? " / " + c.state : ""}${c.zip ? " / " + c.zip : ""}`} — {c.address}</div>
                <div>{ar ? "الدفع" : "Payment"}: {ar ? "كي نت (KNET)" : "KNET"}</div>
                <div className="flex items-center gap-1 text-accent"><Check className="w-3 h-3" /> {ar ? "تم اعتماد المقاسات والموافقة على الشروط وسياسة التعديل خلال 7 أيام" : "Measurements confirmed and terms & 7-day alteration policy accepted"}</div>
              </div>
              <button onClick={() => setEditing(editing === i.lid ? null : i.lid)} data-testid={`cart-edit-${i.product_id}`} className="text-xs text-accent underline mt-2">{ar ? "تعديل المقاسات" : "Edit measurements"}</button>
              {editing === i.lid && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {MEASURE_FIELDS.map((f) => (
                    <div key={f.key}><label className="text-xs text-text-muted">{ar ? f.ar : f.en || f.ar}</label>
                      <input type="number" defaultValue={i.measurements[f.key]} data-testid={`edit-${f.key}-${i.product_id}`}
                        onBlur={(e) => update(i.lid, { measurements: { ...i.measurements, [f.key]: Number(e.target.value) } })}
                        className="w-full mt-1 border border-border px-2 py-1.5 text-sm" /></div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-surface p-5 mt-6 space-y-2 text-sm">
        <div className="flex justify-between"><span>{ar ? "المجموع (قيمة المنتجات)" : "Subtotal"}</span><span data-testid="cart-subtotal">{formatKWD(subtotal)}</span></div>
        <p className="text-[11px] text-text-muted" data-testid="cart-fee-note">{ar ? "تُحتسب رسوم التوصيل أو الشحن وتظهر في ملخص الطلب قبل الدفع." : "Delivery/shipping fees are calculated and shown in the order summary before payment."}</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-3 text-xs">
        <Link to="/policies" className="underline text-accent" data-testid="cart-policy-link">{ar ? "السياسات" : "Policies"}</Link>
        <Link to="/shop" className="underline text-text-muted">{ar ? "متابعة التسوق" : "Continue shopping"}</Link>
      </div>

      <button onClick={() => nav("/checkout")} data-testid="cart-checkout-btn" className="btn-primary w-full mt-5 py-4 text-sm tracking-wide">
        {ar ? "متابعة الدفع كزائرة" : "Continue to checkout as guest"}
      </button>
      <p className="text-[11px] text-text-muted text-center mt-2">{ar ? "لا حاجة لتسجيل الدخول — يمكنك إتمام طلبك كزائرة." : "No login needed — you can complete your order as a guest."}</p>
    </div>
  );
}
